import { useEffect, useRef } from 'react'

import { MetricCard, ScalrCarousel } from '@/components/customComponents'
import FilterBar from '@/components/FilterBar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import { metricCardColors } from '@/constants/COLOR_PALETTE'
import { useCockpitMetrics, useToast } from '@/hooks'
import { addAlert, useAppDispatch, useReduxBaseFilter } from '@/store'
import { dateToString } from '@/utils'
import { addSampleAlerts } from '@/utils/alertDemo'
import { DateType } from '@/utils/date'
import {
  IconBrandSpeedtest,
  IconCashBanknote,
  IconCurrencyDollar,
  IconPercentage,
  IconStars,
} from '@tabler/icons-react'

function DashboardContent() {
  const dispatch = useAppDispatch()
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (!hasInitialized.current) {
      addSampleAlerts({
        addAlert: (alert: Parameters<typeof addAlert>[0]) =>
          dispatch(addAlert(alert)),
      } as any)
      hasInitialized.current = true
    }
  }, [dispatch])

  const { selectedBranch, date, dateFilterType } = useReduxBaseFilter()

  const { toast } = useToast()

  const { branchData, companyData, metricCardData, loading, error } =
    useCockpitMetrics(
      dateToString(date, 'from'),
      dateToString(date, 'to'),
      dateFilterType === 'test-range'
        ? 'month-to-date'
        : (dateFilterType as DateType),
      selectedBranch
    )

  useEffect(() => {
    if (error) {
      toast({
        title: 'Something went wrong.',
        description: error.message,
        variant: 'destructive',
      })
    }
  }, [branchData, error])

  return (
    <Card className="w-full h-full">
      <CardHeader>
        <CardTitle>Dashboard</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <FilterBar pageId="dashboard" />

        <div className="space-y-6">
          <div className="space-y-3 mb-10">
            <ScalrCarousel>
              <MetricCard
                title="ADNS"
                subTitle="Average Daily Sales"
                mtdCurrContent={metricCardData?.currMetricsMonthly?.adns}
                ytdCurrContent={metricCardData?.currMetricsYearly?.adns}
                mtdPrevContent={metricCardData?.prevMetricsMonthly?.adns}
                ytdPrevContent={metricCardData?.prevMetricsYearly?.adns}
                content={companyData?.adns}
                type="money"
                icon={<IconCurrencyDollar color="white" size={20} />}
                color={metricCardColors.blue500}
                loading={loading}
              />
              <MetricCard
                title="CoGS"
                subTitle="Cost of Goods Sold"
                mtdCurrContent={metricCardData?.currMetricsMonthly?.cogsPti}
                ytdCurrContent={metricCardData?.currMetricsYearly?.cogsPti}
                mtdPrevContent={metricCardData?.prevMetricsMonthly?.cogsPti}
                ytdPrevContent={metricCardData?.prevMetricsYearly?.cogsPti}
                content={companyData?.cogsPti}
                type="money"
                icon={<IconCashBanknote color="white" size={20} />}
                color={metricCardColors.red500}
                loading={loading}
                isIncreasingPositive={false}
              />
              <MetricCard
                title="Labor Ratio"
                subTitle="Store Labor Ratio Over Revenue"
                mtdCurrContent={metricCardData?.currMetricsMonthly?.laborRatio}
                ytdCurrContent={metricCardData?.currMetricsYearly?.laborRatio}
                mtdPrevContent={metricCardData?.prevMetricsMonthly?.laborRatio}
                ytdPrevContent={metricCardData?.prevMetricsYearly?.laborRatio}
                content={companyData?.laborRatio}
                type="percentage"
                icon={<IconPercentage color="white" size={20} />}
                color={metricCardColors.green500}
                loading={loading}
                isIncreasingPositive={false}
              />
              <MetricCard
                title="Speed of Service"
                subTitle="Average Time to Serve"
                mtdCurrContent={
                  metricCardData?.currMetricsMonthly?.speedOfService
                }
                ytdCurrContent={
                  metricCardData?.currMetricsYearly?.speedOfService
                }
                mtdPrevContent={
                  metricCardData?.prevMetricsMonthly?.speedOfService
                }
                ytdPrevContent={
                  metricCardData?.prevMetricsYearly?.speedOfService
                }
                content={companyData?.speedOfService}
                type="number"
                icon={<IconBrandSpeedtest color="white" size={20} />}
                color={metricCardColors.orange500}
                loading={loading}
                isIncreasingPositive={false}
              />
              <MetricCard
                title="Google Reviews"
                subTitle="Guest Satisfaction"
                mtdCurrContent={
                  metricCardData?.currMetricsMonthly?.guestSatisfaction
                }
                ytdCurrContent={
                  metricCardData?.currMetricsYearly?.guestSatisfaction
                }
                mtdPrevContent={
                  metricCardData?.prevMetricsMonthly?.guestSatisfaction
                }
                ytdPrevContent={
                  metricCardData?.prevMetricsYearly?.guestSatisfaction
                }
                content={companyData?.guestSatisfaction}
                type="number"
                icon={<IconStars color="white" size={20} />}
                color={metricCardColors.blue300}
                loading={loading}
              />
            </ScalrCarousel>
          </div>

          {/* <div>
            <DataTable
              columns={groupedCockpitMetricsColumns}
              data={[companyData, ...branchData].filter(
                (item): item is CockpitMetricsModel => item !== undefined
              )}
              selectedBranch={selectedBranch}
              cardTitle="Branch Metrics"
              cardDescription="General Branch Metrics"
              tableViewOption={true}
              loading={loading}
            />
          </div> */}
        </div>
      </CardContent>
    </Card>
  )
}

export default function DashboardPage() {
  return <DashboardContent />
}
